import { PreTailoringSkeleton } from '@/components/elements/_skeletons/pre-tailoring.skeleton';
import { BuildingBlocks } from '@/components/elements/building-blocks/building-blocks.component';
import { BuildingBlocksHeading } from '@/components/elements/building-blocks/building-blocks.heading';
import { BuildingBlockDetails } from '@/components/elements/building-blocks/details-building-block/details-building-block.component';
import { Topbar } from '@/components/elements/topbar/topbar.component';
import { TailoringLayout } from '@/components/layout/tailoring/tailoring.layout.component';
import { Answer, ProductBuildingBlock, Template, YesNoQuestion } from '@/gql/graphql';
import { useBuildingBlocks } from '@/hooks/use-building-blocks/use-building-blocks.hook';
import { useQuestions } from '@/hooks/use-questions/use-questions.hook';
import { useUser } from '@/hooks/use-user/use-user.hook';
import { isTemplateTailoringEnabled } from '@/lib/template.utils';
import { useDialogStore } from '@/store/dialog.store';
import { useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { QuestionsHeading } from '../../../components/elements/questions/questions-heading.component';
import { Questions } from '../../../components/elements/questions/questions.component';
import { pretailoringPageMessages } from '../../products/pre-tailoring/pre-tailoring.messages';
import { getTemplate } from '../templates.requests';
import { templatePretailorTopbarConfig } from './templates-pre-tailoring.page.config';

export const TemplatesPretailoringPage = () => {
  const t = useIntl();

  const { guid } = useUser();
  const { id } = useParams();
  const navigate = useNavigate();

  const { openDialog } = useDialogStore((state) => state);

  const { data, loading } = useQuery<{ template: Template }>(getTemplate, {
    variables: { id },
  });

  const { progress, search, filter, radioQuestions, updateTemplateQuestion, handleQuestionFilter, answerInProgress } =
    useQuestions({
      tailoringProcess: data?.template?.tailoringProcess,
      id: data?.template?.id,
    });

  const {
    includedBuildingBlocks,
    excludedBuildingBlocks,
    sort,
    handleSortChange: onSortChange,
    selectedTab,
    setSelectedTab: handleTabChange,
  } = useBuildingBlocks({
    buildingBlocks: data?.template.tailoringProcess.buildingBlocks,
  });

  const onQuestionAnswer = async (question: YesNoQuestion, value: string) => {
    updateTemplateQuestion(question, value.toUpperCase() as Answer, data?.template);
  };

  const handleMoreInfo = (bb: ProductBuildingBlock) => {
    openDialog({
      type: 'custom',
      fullScreen: true,
      title: t.formatMessage(pretailoringPageMessages.singleBuildingBlockModalTitle, {
        name: bb.name,
      }),
      element: (
        <div className="relative inline-flex flex-1 overflow-scroll">
          <div className="absolute top-0 left-0 right-0 flex flex-col gap-3">
            <BuildingBlockDetails buildingBlock={bb} />
          </div>
        </div>
      ),
    });
  };

  return (
    <TailoringLayout
      loading={loading}
      loadingElement={<PreTailoringSkeleton />}
      topbar={<Topbar config={templatePretailorTopbarConfig(navigate, progress, id || '')} />}
      leftAreaFixed={
        <QuestionsHeading
          context="pre-tailoring"
          id={id || ''}
          search={search}
          filter={filter}
          progress={progress}
          data={data?.template}
          name={data?.template?.name || ''}
          status={data?.template?.publicationStatus}
          onFilterSearchChange={handleQuestionFilter}
        />
      }
      leftArea={
        data?.template && (
          <Questions
            unanswered
            disabled={!isTemplateTailoringEnabled(guid, data.template)}
            radioQuestions={radioQuestions}
            handleQuestionAnswer={onQuestionAnswer}
          />
        )
      }
      rightAreaFixed={
        <BuildingBlocksHeading
          sort={sort}
          loading={answerInProgress}
          onTabChange={handleTabChange}
          handleSortChange={onSortChange}
          includedBuildingBlocks={includedBuildingBlocks}
          excludedBuildingBlocks={excludedBuildingBlocks}
        />
      }
      rightArea={
        <BuildingBlocks
          sort={sort}
          selectedTab={selectedTab}
          onBlockClick={handleMoreInfo}
          includedBuildingBlocks={includedBuildingBlocks}
          excludedBuildingBlocks={excludedBuildingBlocks}
          enableInclusion={false}
        />
      }
    />
  );
};
